import React from 'react';
import { useT } from '../../i18n/useT';
import { Helmet } from 'react-helmet-async';
import LegalPage, { LegalSection, LegalPlaceholder, LegalNote } from '../../components/legal/LegalPage';
import '../../components/legal/LegalPage.css';

export default function PrivacyPage() {
  const t = useT();
  const lp = t.legalPages;
  return (
    <>
      <Helmet>
        <title>{lp.privacy.title} – Docudi</title>
        <meta name="description" content={lp.privacy.metaDesc} />
        <meta name="robots" content="noindex" />
      </Helmet>

      <LegalPage title={lp.privacy.title} lastUpdated="[PLACEHOLDER: Date]">

        <LegalSection id="controller" title="1. Controller (Data Controller)">
          <div className="legal-section__content">
            <p>
              The controller responsible for the processing of personal data under Art. 4 (7) GDPR is:
            </p>
            <p>
              <LegalPlaceholder label="Company name" /><br />
              <LegalPlaceholder label="Street address" /><br />
              <LegalPlaceholder label="Postal code, City, Country" /><br />
              Email: <LegalPlaceholder label="contact@example.com" /><br />
              Phone: <LegalPlaceholder label="+49 000 000000" />
            </p>
            <LegalNote>
              Replace all placeholders with your actual company information before publishing.
              Your privacy policy must accurately identify the legal entity that controls user data.
            </LegalNote>
          </div>
        </LegalSection>

        <LegalSection id="data-collected" title="2. Data We Collect">
          <div className="legal-section__content">
            <h3>2.1 Account Data</h3>
            <ul>
              <li>Email address (required for registration)</li>
              <li>Name (optional)</li>
              <li>Password (stored as bcrypt hash — we never store plain-text passwords)</li>
            </ul>

            <h3>2.2 Documents & Files</h3>
            <ul>
              <li>Photos and scanned documents you upload to the app</li>
              <li>Document metadata (file name, creation date, size)</li>
              <li>Tags and folder structures you create</li>
            </ul>

            <h3>2.3 Usage Data</h3>
            <ul>
              <li>App usage events (feature interactions, session duration)</li>
              <li>Device type and operating system</li>
              <li>App version</li>
            </ul>

            <h3>2.4 Technical Data</h3>
            <ul>
              <li>IP address (for security and fraud prevention)</li>
              <li>Log files and error reports</li>
              <li>Cookies and similar tracking technologies (see our <a href="/cookie-policy">Cookie Policy</a>)</li>
            </ul>
          </div>
        </LegalSection>

        <LegalSection id="legal-bases" title="3. Legal Bases for Processing (Art. 6 GDPR)">
          <div className="legal-section__content">
            <ul>
              <li>
                <strong>Contract performance (Art. 6(1)(b) GDPR):</strong> Processing your account data and documents
                is necessary to provide the service you signed up for.
              </li>
              <li>
                <strong>Legal obligation (Art. 6(1)(c) GDPR):</strong> We may be required to retain certain data
                for legal compliance (e.g., tax records, invoices).
              </li>
              <li>
                <strong>Legitimate interests (Art. 6(1)(f) GDPR):</strong> Security monitoring, fraud prevention, and
                product improvement. Our interests do not override your fundamental rights.
              </li>
              <li>
                <strong>Consent (Art. 6(1)(a) GDPR):</strong> For optional analytics and marketing cookies, where you
                have given explicit consent via our cookie banner.
              </li>
            </ul>
            <LegalNote>
              Verify that each processing activity is mapped to the correct legal basis and documented
              in your Record of Processing Activities (RoPA) as required by Art. 30 GDPR.
            </LegalNote>
          </div>
        </LegalSection>

        <LegalSection id="processors" title="4. Third-Party Processors & Transfers">
          <div className="legal-section__content">
            <p>
              We use the following third-party service providers (processors) who process personal data on our behalf.
              All processors are bound by Data Processing Agreements (DPAs) in accordance with Art. 28 GDPR.
            </p>
            <ul>
              <li><LegalPlaceholder label="Cloud storage provider (e.g. AWS / GCP / Hetzner)" /> — document storage and infrastructure</li>
              <li><LegalPlaceholder label="Email provider (e.g. Postmark / SendGrid)" /> — transactional emails</li>
              <li><LegalPlaceholder label="Analytics provider (e.g. Plausible / Mixpanel)" /> — anonymised usage analytics (only with consent)</li>
              <li><LegalPlaceholder label="Payment provider (e.g. Stripe)" /> — payment processing for subscriptions</li>
              <li><LegalPlaceholder label="Error monitoring (e.g. Sentry)" /> — crash and error reporting</li>
            </ul>
            <p>
              <LegalPlaceholder label="Note any EU/non-EU transfers and relevant safeguards (SCCs, adequacy decisions)." />
            </p>
          </div>
        </LegalSection>

        <LegalSection id="retention" title="5. Retention Periods">
          <div className="legal-section__content">
            <ul>
              <li>Account data: retained for the duration of your account, plus <LegalPlaceholder label="X months/years" /> after deletion to comply with legal obligations.</li>
              <li>Documents: deleted immediately upon your request or account deletion.</li>
              <li>Billing records: retained for <LegalPlaceholder label="X years" /> to meet tax/commercial law requirements.</li>
              <li>Log files: automatically deleted after <LegalPlaceholder label="X days" />.</li>
              <li>Analytics data: <LegalPlaceholder label="describe retention" />.</li>
            </ul>
          </div>
        </LegalSection>

        <LegalSection id="your-rights" title="6. Your Rights Under GDPR">
          <div className="legal-section__content">
            <p>You have the following rights regarding your personal data:</p>
            <ul>
              <li><strong>Right of access (Art. 15 GDPR):</strong> Request a copy of the data we hold about you.</li>
              <li><strong>Right to rectification (Art. 16 GDPR):</strong> Correct inaccurate or incomplete data.</li>
              <li><strong>Right to erasure / "right to be forgotten" (Art. 17 GDPR):</strong> Request deletion of your data. See our <a href="/data-deletion">Data Deletion Guide</a>.</li>
              <li><strong>Right to restriction (Art. 18 GDPR):</strong> Restrict how we process your data in certain circumstances.</li>
              <li><strong>Right to data portability (Art. 20 GDPR):</strong> Receive your data in a machine-readable format.</li>
              <li><strong>Right to object (Art. 21 GDPR):</strong> Object to processing based on legitimate interests or direct marketing.</li>
              <li><strong>Right to withdraw consent:</strong> Where processing is based on consent, you may withdraw it at any time without affecting prior processing.</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at: <LegalPlaceholder label="privacy@example.com" />
            </p>
            <p>
              You also have the right to lodge a complaint with the supervisory authority in your EU member state.
              In Germany: <LegalPlaceholder label="Name and URL of relevant Landesbeauftragter" />.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="security" title="7. Data Security">
          <div className="legal-section__content">
            <p>
              We implement appropriate technical and organisational security measures to protect your personal
              data against unauthorised access, disclosure, alteration, or destruction. These include:
            </p>
            <ul>
              <li>Encryption in transit (TLS 1.3)</li>
              <li>Encryption at rest (<LegalPlaceholder label="describe algorithm, e.g. AES-256" />)</li>
              <li>Access control and least-privilege policies</li>
              <li>Regular security audits (<LegalPlaceholder label="if applicable" />)</li>
            </ul>
          </div>
        </LegalSection>

        <LegalSection id="minors" title="8. Children's Privacy">
          <div className="legal-section__content">
            <p>
              Docudi is not directed at children under the age of <LegalPlaceholder label="13 / 16 — verify by jurisdiction" />.
              We do not knowingly collect personal data from children. If you believe a child has provided us with
              personal data, please contact us immediately at <LegalPlaceholder label="privacy@example.com" /> and
              we will delete it.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="changes" title="9. Changes to This Policy">
          <div className="legal-section__content">
            <p>
              We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated"
              date at the top of this page and, where changes are material, notify registered users by email.
              Continued use of the service after the effective date of changes constitutes acceptance of the
              updated policy.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="contact" title="10. Contact">
          <div className="legal-section__content">
            <p>
              For all privacy-related enquiries:<br />
              <LegalPlaceholder label="Company name" /><br />
              <LegalPlaceholder label="Address" /><br />
              Email: <LegalPlaceholder label="privacy@example.com" />
            </p>
            <p>
              Data Protection Officer (if applicable):<br />
              <LegalPlaceholder label="DPO name and contact — remove section if not required" />
            </p>
          </div>
        </LegalSection>

      </LegalPage>
    </>
  );
}
